import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, Trophy, MessageCircle, Heart, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

/**
 * AnniversaryReunion — Sprint 199
 * ────────────────────────────────
 * Pure brand-love. Lands the user here from the yearly "it's been N years"
 * email/SMS. Shows a journey snapshot with two CTAs:
 *   • Take a peek  — short-lived JWT, dashboard stays read-only.
 *   • Wake my account up — full reactivate.
 *
 *  URL:  /anniversary-reunion?token=…
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';
const GOLD = '#FFD700';

const AnniversaryReunion = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');
  const [phase, setPhase] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('Reunion link is missing its token.');
      return;
    }
    let cancelled = false;
    apiClient.request(`/api/anniversary-reunion/preview?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Reunion link no longer valid.');
        setPhase('invalid');
      });
    return () => { cancelled = true; };
  }, [token]);

  const act = async (action) => {
    setPhase('acting');
    try {
      const res = await apiClient.request('/api/anniversary-reunion/action', {
        method: 'POST',
        body: JSON.stringify({ token, action }),
      });
      if (login && res.access_token) {
        await login({ access_token: res.access_token, user: { user_id: res.user_id, name: res.name } });
      } else if (res.access_token) {
        localStorage.setItem('hibff_auth_token', res.access_token);
      }
      navigate(res.next_step || '/');
    } catch (err) {
      setError(err?.message || 'Could not open your account.');
      setPhase('ready');
    }
  };

  const first = data?.name?.split(' ')[0] || 'friend';
  const years = data?.years_paused || 1;
  const yearWord = years === 1 ? 'year' : 'years';
  const firstJoined = data?.first_joined_at ? new Date(data.first_joined_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long' }) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden" data-testid="anniversary-page">

        {phase === 'loading' && (
          <div className="text-center py-20"><Loader2 size={40} className="animate-spin mx-auto" style={{ color: PURPLE }} /></div>
        )}

        {phase === 'invalid' && (
          <div className="text-center py-12 px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: '#FEF3F2' }}>
              <AlertTriangle size={28} style={{ color: '#B42318' }} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Reunion link can&apos;t be used</h1>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        )}

        {(phase === 'ready' || phase === 'acting') && data && (
          <>
            {/* Warm hero — the moment of magic */}
            <div
              className="px-6 sm:px-10 py-12 text-white text-center relative overflow-hidden"
              style={{ background: `linear-gradient(140deg, #1A0F2E 0%, ${PURPLE} 60%, ${ORANGE} 100%)` }}
            >
              <div className="absolute inset-0" style={{
                background: 'radial-gradient(circle at 20% 20%, rgba(255,215,0,0.25), transparent 45%), radial-gradient(circle at 80% 80%, rgba(255,99,132,0.20), transparent 50%)',
                pointerEvents: 'none',
              }} />
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 mb-3 text-[11px] uppercase tracking-wider font-bold">
                  <Sparkles size={12} style={{ color: GOLD }} /> {years} {yearWord} on
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold mb-2" data-testid="anniversary-heading">
                  Welcome back, {first}.
                </h1>
                <p className="text-white/85 text-sm sm:text-base max-w-md mx-auto">
                  It&apos;s been {years} {yearWord} since you paused HiBFF. We kept everything exactly as you left it.
                </p>
              </div>
            </div>

            {/* Journey snapshot */}
            <div className="px-6 sm:px-10 py-8">
              <p className="text-center text-sm text-gray-500 mb-5">Here&apos;s where you got to.</p>

              <div className="grid grid-cols-3 gap-3 mb-6" data-testid="anniversary-stats">
                <Stat icon={<Trophy size={18} style={{ color: GOLD }} />} label="Trophies" value={data.trophy_count} />
                <Stat icon={<MessageCircle size={18} style={{ color: PURPLE }} />} label="AvA chats" value={data.chat_count} />
                <Stat icon={<Heart size={18} fill="#FF6384" stroke="#FF6384" />} label="Hearts" value={data.hearts_balance} />
              </div>

              {firstJoined && (
                <div className="rounded-xl p-3 mb-6 flex items-center gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200">
                  <Calendar size={14} style={{ color: PURPLE }} />
                  <span>You first joined HiBFF in <strong>{firstJoined}</strong>. That&apos;s a long road.</span>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' }}>{error}</div>
              )}

              <div className="space-y-2.5">
                <button
                  onClick={() => act('peek')}
                  disabled={phase === 'acting'}
                  className="w-full px-6 py-4 rounded-full font-bold text-sm text-white disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99] inline-flex items-center justify-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2BB8)`, boxShadow: `0 4px 18px ${PURPLE}55` }}
                  data-testid="anniversary-peek-btn"
                >
                  Take a peek at how far you&apos;ve come
                  <ArrowRight size={14} />
                </button>
                <button
                  onClick={() => act('wake')}
                  disabled={phase === 'acting'}
                  className="w-full px-6 py-3.5 rounded-full font-bold text-sm disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99]"
                  style={{ background: '#F5F3FF', color: PURPLE, border: `1px solid ${PURPLE}40` }}
                  data-testid="anniversary-wake-btn"
                >
                  {phase === 'acting' ? <><Loader2 size={14} className="inline animate-spin mr-2" /> Waking…</> : 'Wake my account up'}
                </button>
              </div>

              <p className="text-[11px] text-gray-500 text-center mt-5">
                Peeking is free and keeps your account exactly as it is. Waking up reactivates your subscription on the next billing cycle.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="rounded-xl p-3 text-center bg-gradient-to-br from-purple-50 to-orange-50 border border-purple-100">
    <div className="flex justify-center mb-1.5">{icon}</div>
    <div className="text-2xl font-extrabold text-gray-900 tabular-nums">{(value || 0).toLocaleString()}</div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
  </div>
);

export default AnniversaryReunion;
